import { singletonId } from '../ids';
import type { Mutation } from '../mutations';
import type { BudgetSettings } from '../rules/readModel';
import type { BuildCtx } from '../spending/types';
import { budgetMutation } from './types';

/** Spec §6: "Deduct spending from my budget automatically" is on by default; no default fund yet. */
export const DEFAULT_BUDGET_SETTINGS: Required<BudgetSettings> = {
  autoDeduct: true,
  defaultFundId: null,
  // v0.5: funds are off until the user turns them on (Ivan, 2026-11-03).
  useFunds: false,
  defaultAccountId: null,
  incomeAccountId: null,
};

/** Funds are in use unless the setting says otherwise (see BudgetSettings.useFunds). */
export const fundsInUse = (s: Pick<BudgetSettings, 'useFunds'>): boolean => s.useFunds !== false;

/** Conventions §3: one row per user, id = singletonId(userId, 'budget_settings'). */
export function budgetSettingsId(userId: string): string {
  return singletonId(userId, 'budget_settings');
}

/** The seed row, insert-if-absent so it never resets a choice made on another device. */
export function buildSeedBudgetSettings(userId: string, ctx: BuildCtx): Mutation {
  return budgetMutation(
    ctx,
    'budget_settings',
    'insert',
    budgetSettingsId(userId),
    { ...DEFAULT_BUDGET_SETTINGS },
    { ifAbsent: true },
  );
}

/**
 * Changes only the given fields. If this device has no row yet, the seed goes first (insert-if-absent),
 * then the update, so a row made on another device keeps its other fields.
 */
export function buildBudgetSettingsPatch(
  userId: string,
  rowExists: boolean,
  patch: Partial<BudgetSettings>,
  ctx: BuildCtx,
): Mutation[] {
  const values: Record<string, unknown> = {};
  if (patch.autoDeduct !== undefined) values.autoDeduct = patch.autoDeduct;
  if (patch.defaultFundId !== undefined) values.defaultFundId = patch.defaultFundId;
  if (patch.useFunds !== undefined) values.useFunds = patch.useFunds;
  if (patch.defaultAccountId !== undefined) values.defaultAccountId = patch.defaultAccountId;
  if (patch.incomeAccountId !== undefined) values.incomeAccountId = patch.incomeAccountId;
  if (Object.keys(values).length === 0) return [];
  const seed = rowExists ? [] : [buildSeedBudgetSettings(userId, ctx)];
  const update = budgetMutation(ctx, 'budget_settings', 'update', budgetSettingsId(userId), values);
  return [...seed, update];
}

/**
 * The fund a new expense deducts from (spec §6): the fund picked on the entry, otherwise the default
 * fund when auto-deduct is on, otherwise none. Changing the setting never touches past entries,
 * because this only runs for expense.created.
 */
export function pickDeductFund(
  pickedFundId: string | null | undefined,
  settings: BudgetSettings,
): string | null {
  // v0.5: with funds off nothing deducts, picked or default (fund rows are kept for later).
  if (!fundsInUse(settings)) return null;
  if (pickedFundId) return pickedFundId;
  return settings.autoDeduct ? settings.defaultFundId : null;
}

/**
 * The fund a producer writes into a new expense's insert (spec §6, §8): pickDeductFund while Budget
 * is usable, otherwise none (a picked fund only deducts while Budget is usable). Writing it in the
 * insert, not in a follow-up update, means the fund can't lose to the insert when the server
 * clamps a fast clock and both writes land with the same time.
 */
export function resolveDeductFund(
  pickedFundId: string | null | undefined,
  settings: BudgetSettings,
  budgetUsable: boolean,
): string | null {
  return budgetUsable ? pickDeductFund(pickedFundId, settings) : null;
}
